import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Clock, Coffee, Loader2, Moon, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import {
  ALL_WEEKDAYS,
  WEEKDAY_LETTERS,
  formatHours,
  minutesOfDay,
  netHours,
  normalizeWeekdays,
  shiftTimesProblem,
  spanMinutes,
  weekdaysLabel,
  wrapsMidnight,
} from '@/lib/shiftHours'
import { resourceCenterApi, type ShiftDefinition } from '@/api/resourceCenterApi'
import { useInvalidateResourceCenter, useShiftDefinitions } from '@/hooks/useResourceCenter'
import { shiftTone, type ShiftTone } from './shiftTones'

interface Draft {
  code: string
  name: string
  startTime: string
  endTime: string
  breakMinutes: string
  weekdays: number[]
}

const EMPTY: Draft = {
  code: '',
  name: '',
  startTime: '08:00',
  endTime: '16:00',
  breakMinutes: '0',
  weekdays: [...ALL_WEEKDAYS],
}

const toDraft = (s: ShiftDefinition): Draft => ({
  code: s.code,
  name: s.name,
  startTime: s.startTime,
  endTime: s.endTime,
  breakMinutes: String(s.breakMinutes),
  weekdays: normalizeWeekdays(s.weekdays),
})

const messageOf = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.')

/**
 * Center Setup → Shift definitions (z8nrz7f0vu, z8nrz7f7g8): the components a
 * pattern or a profile puts on a day. Each card shows when the shift runs on a
 * 24-hour bar, its net hours (start to end less the break) and the weekdays it
 * may be worked. Adding or editing opens a form with a live preview. One list
 * for the whole center: editing a shift's times changes every resource and
 * pattern on it.
 */
export function ShiftDefinitionsDialog({
  open,
  onOpenChange,
  canManage,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  canManage: boolean
}) {
  const shifts = useShiftDefinitions()
  const [error, setError] = useState<string | null>(null)
  // null = closed; 'new' = adding; otherwise the shift being edited.
  const [editing, setEditing] = useState<ShiftDefinition | 'new' | null>(null)
  const list = shifts.data ?? []
  const ids = list.map(s => s.id)

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        setError(null)
        onOpenChange(o)
      }}
    >
      <DialogContent className='max-h-[90vh] gap-0 overflow-hidden p-0 sm:max-w-3xl' onOpenAutoFocus={e => e.preventDefault()}>
        <DialogHeader className='space-y-1 border-b px-6 pb-4 pt-6 text-left'>
          <div className='flex items-start justify-between gap-4 pr-6'>
            <div className='space-y-1'>
              <DialogTitle className='flex items-center gap-2.5'>
                <span className='flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary'>
                  <Clock className='h-4 w-4' />
                </span>
                Shift definitions
              </DialogTitle>
              <DialogDescription>
                The components patterns and people are scheduled on. Set once for the center — changing a shift's
                times updates everyone on it.
              </DialogDescription>
            </div>
            {canManage && (
              <Button size='sm' className='h-9 shrink-0 rounded-lg' onClick={() => setEditing('new')}>
                <Plus className='mr-1.5 h-4 w-4' /> New shift
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className='max-h-[calc(90vh-7rem)] space-y-2 overflow-y-auto px-6 py-4'>
          {error && <p className='rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive'>{error}</p>}
          {shifts.isLoading && (
            <div className='py-10 text-center'>
              <Loader2 className='mx-auto h-5 w-5 animate-spin text-muted-foreground' />
            </div>
          )}
          {shifts.isSuccess && list.length === 0 && (
            <div className='rounded-2xl border border-dashed px-6 py-12 text-center'>
              <Clock className='mx-auto h-6 w-6 text-muted-foreground' />
              <p className='mt-2 text-sm font-medium'>No shifts yet</p>
              <p className='mt-1 text-xs text-muted-foreground'>
                Add the center's first shift — e.g. DAY 08:00–16:00, every day.
              </p>
              {canManage && (
                <Button size='sm' className='mt-4' onClick={() => setEditing('new')}>
                  <Plus className='mr-1.5 h-4 w-4' /> New shift
                </Button>
              )}
            </div>
          )}
          {list.map(s => (
            <ShiftCard
              key={s.id}
              shift={s}
              tone={shiftTone(ids, s.id)}
              canManage={canManage}
              onEdit={() => setEditing(s)}
              onError={setError}
            />
          ))}
          {list.length > 0 && (
            <div className='flex items-center justify-between px-1 pt-1 text-[10px] text-muted-foreground'>
              <span>Bars show each shift across a 24-hour day</span>
              <span className='tabular-nums'>00 · 06 · 12 · 18 · 24</span>
            </div>
          )}
        </div>

        {editing !== null && (
          <ShiftForm
            key={editing === 'new' ? 'new' : editing.id}
            shift={editing === 'new' ? null : editing}
            tone={editing === 'new' ? shiftTone([...ids, 'new'], 'new') : shiftTone(ids, editing.id)}
            onClose={() => setEditing(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

/** One shift as a card: code badge, name, the 24-hour bar, net hours, days, active. */
function ShiftCard({
  shift,
  tone,
  canManage,
  onEdit,
  onError,
}: {
  shift: ShiftDefinition
  tone: ShiftTone
  canManage: boolean
  onEdit: () => void
  onError: (message: string | null) => void
}) {
  const invalidate = useInvalidateResourceCenter()
  const done = () => {
    onError(null)
    void invalidate()
  }
  const toggle = useMutation({
    mutationFn: (active: boolean) => resourceCenterApi.updateShift(shift.id, { active }),
    onSuccess: done,
    onError: e => onError(messageOf(e)),
  })
  const remove = useMutation({
    mutationFn: () => resourceCenterApi.removeShift(shift.id),
    onSuccess: done,
    onError: e => onError(messageOf(e)),
  })

  return (
    <div
      className={cn(
        'group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 rounded-2xl border bg-background px-4 py-3 shadow-sm transition-all hover:shadow-md',
        !shift.active && 'opacity-60',
      )}
    >
      <span
        className={cn(
          'flex h-11 min-w-[3.25rem] items-center justify-center rounded-xl px-2 font-mono text-sm font-bold text-white',
          shift.active ? tone.chip : 'bg-muted-foreground',
        )}
      >
        {shift.code}
      </span>

      <div className='min-w-0 space-y-1.5'>
        <div className='flex flex-wrap items-baseline gap-x-3 gap-y-0.5'>
          <span className='truncate font-semibold'>{shift.name}</span>
          <span className='text-sm tabular-nums text-muted-foreground'>
            {shift.startTime}–{shift.endTime}
          </span>
          {shift.wrapsMidnight && <OvernightTag />}
          {!shift.active && <span className='text-xs text-muted-foreground'>· inactive</span>}
        </div>
        <TimelineBar startTime={shift.startTime} endTime={shift.endTime} tone={tone} />
        <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground'>
          <span>
            <span className='font-semibold text-foreground tabular-nums'>{formatHours(shift.hours)}</span> net
          </span>
          {shift.breakMinutes > 0 && (
            <span className='flex items-center gap-1'>
              <Coffee className='h-3 w-3' /> {shift.breakMinutes} min break
            </span>
          )}
          <WeekdayDots weekdays={shift.weekdays} />
        </div>
      </div>

      <div className='flex items-center gap-1'>
        <Switch
          checked={shift.active}
          disabled={!canManage || toggle.isPending}
          onCheckedChange={on => toggle.mutate(on)}
          aria-label={shift.active ? `Deactivate ${shift.code}` : `Reactivate ${shift.code}`}
          title={shift.active ? 'Active — offered in patterns and profiles' : 'Inactive — no longer offered'}
        />
        {canManage && (
          <>
            <Button variant='ghost' size='icon' className='h-8 w-8' aria-label={`Edit ${shift.code}`} onClick={onEdit}>
              <Pencil className='h-4 w-4' />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8 text-muted-foreground hover:text-destructive'
              aria-label={`Delete ${shift.code}`}
              title='Delete — refused while a pattern uses it'
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Delete shift ${shift.code}? This is refused while a pattern uses it.`)) remove.mutate()
              }}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

/** Add or edit a shift, with a live preview of what will be saved. */
function ShiftForm({ shift, tone, onClose }: { shift: ShiftDefinition | null; tone: ShiftTone; onClose: () => void }) {
  const invalidate = useInvalidateResourceCenter()
  const [draft, setDraft] = useState<Draft>(shift ? toDraft(shift) : EMPTY)
  const [error, setError] = useState<string | null>(null)

  const breakMinutes = Number(draft.breakMinutes || 0)
  const timesSet = !!draft.startTime && !!draft.endTime
  const problem = timesSet ? shiftTimesProblem(draft.startTime, draft.endTime, breakMinutes) : null
  const hours = timesSet && !problem ? netHours(draft.startTime, draft.endTime, breakMinutes) : null
  const span = timesSet && !problem ? spanMinutes(draft.startTime, draft.endTime) : null
  const overnight = timesSet && wrapsMidnight(draft.startTime, draft.endTime)
  const complete =
    !!draft.code.trim() && !!draft.name.trim() && timesSet && !problem && draft.weekdays.length > 0
  const dirty = JSON.stringify(draft) !== JSON.stringify(shift ? toDraft(shift) : EMPTY)

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: draft.code.trim(),
        name: draft.name.trim(),
        startTime: draft.startTime,
        endTime: draft.endTime,
        breakMinutes,
        weekdays: draft.weekdays,
      }
      return shift ? resourceCenterApi.updateShift(shift.id, body) : resourceCenterApi.createShift(body)
    },
    onSuccess: () => {
      void invalidate()
      onClose()
    },
    onError: e => setError(messageOf(e)),
  })

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft(d => ({ ...d, [k]: v }))

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='gap-0 overflow-hidden p-0 sm:max-w-lg'>
        <DialogHeader className='border-b px-6 pb-4 pt-6 text-left'>
          <DialogTitle>{shift ? `Edit ${shift.code}` : 'New shift'}</DialogTitle>
          <DialogDescription>
            {shift
              ? 'Changes apply to every pattern and person on this shift.'
              : 'A shift is a block of work — when it starts and ends, and which days it can be worked.'}
          </DialogDescription>
        </DialogHeader>

        <form
          id='shift-form'
          className='space-y-5 px-6 py-5'
          onSubmit={e => {
            e.preventDefault()
            if (complete && dirty) save.mutate()
          }}
        >
          {error && <p className='rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive'>{error}</p>}

          <div className='grid grid-cols-[7rem_1fr] gap-3'>
            <div className='space-y-1.5'>
              <Label htmlFor='shift-code'>Code</Label>
              <Input
                id='shift-code'
                autoFocus={!shift}
                value={draft.code}
                onChange={e => set('code', e.target.value.toUpperCase())}
                placeholder='DAY'
                maxLength={12}
                className='h-10 font-mono font-semibold tracking-wide'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='shift-name'>Name</Label>
              <Input
                id='shift-name'
                value={draft.name}
                onChange={e => set('name', e.target.value)}
                placeholder='e.g. Day 12h'
                className='h-10'
              />
            </div>
          </div>

          <div className='grid grid-cols-3 gap-3'>
            <div className='space-y-1.5'>
              <Label htmlFor='shift-start'>Starts</Label>
              <Input
                id='shift-start'
                type='time'
                value={draft.startTime}
                onChange={e => set('startTime', e.target.value)}
                className='h-10'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='shift-end'>Ends</Label>
              <Input
                id='shift-end'
                type='time'
                value={draft.endTime}
                onChange={e => set('endTime', e.target.value)}
                className='h-10'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='shift-break'>Break</Label>
              <div className='relative'>
                <Input
                  id='shift-break'
                  type='number'
                  min={0}
                  step={5}
                  value={draft.breakMinutes}
                  onChange={e => set('breakMinutes', e.target.value)}
                  className='h-10 pr-11'
                />
                <span className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground'>
                  min
                </span>
              </div>
            </div>
          </div>

          <div className='space-y-1.5'>
            <div className='flex items-center justify-between'>
              <Label>Days it can be worked</Label>
              <div className='flex gap-1 text-xs'>
                {[
                  { label: 'Every day', days: [...ALL_WEEKDAYS] },
                  { label: 'Mon–Fri', days: [1, 2, 3, 4, 5] },
                  { label: 'Weekend', days: [0, 6] },
                ].map(p => (
                  <button
                    key={p.label}
                    type='button'
                    onClick={() => set('weekdays', p.days)}
                    className='rounded-full px-2 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground'
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div role='group' aria-label='Days' className='grid grid-cols-7 gap-1.5'>
              {WEEKDAY_LETTERS.map((name, d) => {
                const on = draft.weekdays.includes(d)
                return (
                  <button
                    key={name}
                    type='button'
                    aria-pressed={on}
                    onClick={() =>
                      set('weekdays', normalizeWeekdays(on ? draft.weekdays.filter(x => x !== d) : [...draft.weekdays, d]))
                    }
                    className={cn(
                      'h-10 rounded-lg border text-xs font-semibold transition-colors',
                      on ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {name}
                  </button>
                )
              })}
            </div>
            {draft.weekdays.length === 0 && <p className='text-xs text-destructive'>Pick at least one day.</p>}
          </div>

          {/* Live preview of what will be saved. */}
          <div className='space-y-2 rounded-xl bg-muted/50 p-4'>
            <div className='flex items-center gap-3'>
              <span
                className={cn(
                  'flex h-9 min-w-[3rem] items-center justify-center rounded-lg px-2 font-mono text-xs font-bold text-white',
                  tone.chip,
                )}
              >
                {draft.code.trim() || '—'}
              </span>
              <div className='min-w-0 flex-1'>
                <p className='truncate text-sm font-medium'>{draft.name.trim() || 'Untitled shift'}</p>
                <p className='text-xs text-muted-foreground'>
                  {problem ? (
                    <span className='text-destructive'>{problem}</span>
                  ) : hours !== null && span !== null ? (
                    <>
                      <span className='font-semibold text-foreground'>{formatHours(hours)} net</span> ·{' '}
                      {formatHours(span / 60)} on site{breakMinutes > 0 && ` − ${breakMinutes} min break`} ·{' '}
                      {weekdaysLabel(draft.weekdays)}
                    </>
                  ) : (
                    'Set a start and end'
                  )}
                </p>
              </div>
              {overnight && !problem && <OvernightTag />}
            </div>
            {timesSet && !problem && <TimelineBar startTime={draft.startTime} endTime={draft.endTime} tone={tone} />}
          </div>
        </form>

        <DialogFooter className='border-t bg-muted/30 px-6 py-3'>
          <Button type='button' variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' form='shift-form' disabled={!complete || !dirty || save.isPending}>
            {save.isPending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
            {shift ? 'Save changes' : 'Add shift'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** "Ends the next day". */
function OvernightTag() {
  return (
    <span
      className='inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700'
      title='Ends the next day'
    >
      <Moon className='h-3 w-3' /> Overnight
    </span>
  )
}

/**
 * Where a shift sits in a 24-hour day. An overnight shift is drawn as two
 * pieces: from its start to midnight, and from midnight to its end.
 */
function TimelineBar({ startTime, endTime, tone }: { startTime: string; endTime: string; tone: ShiftTone }) {
  const start = minutesOfDay(startTime)
  const end = minutesOfDay(endTime)
  if (start === null || end === null) return null
  const pct = (m: number) => `${(m / 1440) * 100}%`
  const pieces = end > start ? [[start, end]] : [[start, 1440], [0, end]]
  return (
    <div className='relative h-2 w-full overflow-hidden rounded-full bg-muted'>
      {[360, 720, 1080].map(m => (
        <span key={m} className='absolute top-0 h-full w-px bg-background/80' style={{ left: pct(m) }} />
      ))}
      {pieces.map(([a, b]) => (
        <span
          key={a}
          className={cn('absolute top-0 h-full rounded-full', tone.bar)}
          style={{ left: pct(a), width: pct(b - a) }}
        />
      ))}
    </div>
  )
}

/** Seven small day dots, the allowed ones filled. */
function WeekdayDots({ weekdays }: { weekdays: number[] }) {
  return (
    <span className='flex items-center gap-1.5' title={weekdaysLabel(weekdays)}>
      <span className='flex gap-0.5'>
        {WEEKDAY_LETTERS.map((name, d) => (
          <span
            key={name}
            className={cn(
              'flex h-4 w-4 items-center justify-center rounded text-[9px] font-semibold',
              weekdays.includes(d) ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground/50',
            )}
          >
            {name[0]}
          </span>
        ))}
      </span>
      {weekdaysLabel(weekdays)}
    </span>
  )
}
